import { findIndex, find, findLastIndex, filter, sum } from 'ramda';
import { dateTimeStringPattern } from '~~utils/CommonUtils';

const RosterSheetName = '球員名單';
const PAStatusSheetName = '打席資訊';
const PAResultSheetName = '打席結果';
const PABallCountSheetName = '打席用球';

const ballInResult = ['1B', '2B', '3B', 'HR', 'E', 'FC', 'GO', 'FO', 'GIDP', 'SH', 'SF'];
const staticResult = ['uBB', 'HBP', 'IBB', 'SO', 'IR', 'IH', 'ID'];
const dropPoints = ['1', '2', '3', '4', '5', '6', '34', '46', '56', '7', '78', '8', '89', '9', 'x'];
const trajectoryMapping = { '1': 'G', '2': 'F', '3': 'L', x: 'x' };

const pbpPositionToBox = {
  1: 'P',
  2: 'C',
  3: '1B',
  4: '2B',
  5: '3B',
  6: 'SS',
  7: 'LF',
  8: 'CF',
  9: 'RF',
  10: 'DH',
};

const getCellValue = (cell) => {
  if (!cell?.v && cell?.v !== 0) {
    return '';
  }
  return cell.v.toString();
};

export const parseExcel = ({
  workbook,
  errorToast,
  awayTeamPlayers = [],
  homeTeamPlayers = [],
  allowPlayerCreate = false,
}) => {
  const players = {
    away: awayTeamPlayers,
    home: homeTeamPlayers,
  };
  const PbPResult = {
    away: {
      pbp: [],
      fielders: [],
    },
    home: {
      pbp: [],
      fielders: [],
    },
  };

  const parseRoster = (sheet) => {
    const awayTeamName = getCellValue(sheet['A2']);
    const homeTeamName = getCellValue(sheet['C2']);
    const gameSeq = parseInt(getCellValue(sheet['E1']));
    const gameStartedAt = `${getCellValue(sheet['F1'])} ${getCellValue(sheet['G1'])}`;
    const gameEndedAt = `${getCellValue(sheet['I1'])} ${getCellValue(sheet['J1'])}`;
    const gameLocation = getCellValue(sheet['H1']);
    const endOuts = getCellValue(sheet['L1']) ? parseInt(getCellValue(sheet['L1'])) : 3;
    const awayPlayers = [];
    const homePlayers = [];
    const awayScores = [];
    const homeScores = [];

    if (!awayTeamName || !homeTeamName) {
      errorToast(`球員名單 A2 C2 應包含客隊與主隊隊名`);
      return { error: true };
    }

    if (Number.isNaN(gameSeq) || gameSeq < 0) {
      errorToast(`球員名單 E1 應包含比賽編號`);
      return { error: true };
    }

    const dateTimeRegExp = new RegExp(`^${dateTimeStringPattern}$`);
    if (!gameStartedAt.match(dateTimeRegExp) || !gameEndedAt.match(dateTimeRegExp)) {
      errorToast(`球員名單 F1G1 I1J1 應包含起始與結束時間`);
      return { error: true };
    }

    if (!gameLocation) {
      errorToast(`球員名單 H1 應包含比賽地點`);
      return { error: true };
    }

    if (Number.isNaN(endOuts) || 0 > endOuts || 3 < endOuts) {
      errorToast(`球員名單 L1 應為 0 - 3 的數字`);
      return { error: true };
    }

    const minRosterRow = 4;
    const maxRosterRow = 40;
    for (let rowSeq = minRosterRow; rowSeq <= maxRosterRow; rowSeq++) {
      const awayNumber = getCellValue(sheet[`A${rowSeq}`]);
      const awayName = getCellValue(sheet[`B${rowSeq}`]);
      const homeNumber = getCellValue(sheet[`C${rowSeq}`]);
      const homeName = getCellValue(sheet[`D${rowSeq}`]);

      const awayPlayer = awayNumber && awayName ? { name: awayName, number: awayNumber, uniqid: 'NEW' } : null;
      const homePlayer = homeNumber && homeName ? { name: homeName, number: homeNumber, uniqid: 'NEW' } : null;
      if (awayPlayer) {
        awayPlayers.push(awayPlayer);
      }
      if (homePlayer) {
        homePlayers.push(homePlayer);
      }
      if (!awayPlayer && !homePlayer) {
        break;
      }
    }

    if (awayPlayers.length === 0 || homePlayers.length === 0) {
      errorToast(`球員名單 無法獲得任何客隊或主隊球員`);
      return { error: true };
    }

    // prettier-ignore
    const inningColumns = ['G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    for (let columnIndex = 0; columnIndex < inningColumns.length; columnIndex++) {
      const awayRun = getCellValue(sheet[`${inningColumns[columnIndex]}2`]);
      const homeRun = getCellValue(sheet[`${inningColumns[columnIndex]}3`]);
      if (!awayRun) {
        break;
      }

      awayScores.push(awayRun);
      homeScores.push(homeRun ? homeRun : '-');
    }

    if (awayScores.length === 0 || homeScores.length === 0) {
      errorToast(`球員名單 G2 G3 後應該包含比賽逐局分數`);
      return { error: true };
    }

    return {
      away: {
        teamName: awayTeamName,
        players: awayPlayers,
        scores: awayScores,
      },
      home: {
        teamName: homeTeamName,
        players: homePlayers,
        scores: homeScores,
      },
      info: {
        gameStartedAt,
        gameEndedAt,
        gameLocation,
        endOuts,
        gameSeq,
      },
    };
  };

  const parsePAStatus = ({ sheet, inningFrame, targetPlayers, opponentPlayers }) => {
    const pbpRows = [];

    const getBases = (seq) => {
      const first = getCellValue(sheet[`M${seq}`]);
      const second = getCellValue(sheet[`N${seq}`]);
      const third = getCellValue(sheet[`O${seq}`]);
      let bases = 0;
      if (first === '1') {
        bases += 1;
      }
      if (second === '1') {
        bases += 2;
      }
      if (third === '1') {
        bases += 4;
      }

      return bases;
    };

    const getR = (seq, order) => {
      const R = parseInt(getCellValue(sheet[`P${seq}`]));
      const ROrder = parseInt(getCellValue(sheet[`Q${seq}`]));

      if (R !== 1) {
        return { R: 0, is_ER: false };
      }

      // 預設 true
      const is_ER = parseInt(getCellValue(sheet[`S${seq}`])) !== 0;
      if (1 <= ROrder && ROrder <= 9) {
        return { R: ROrder, is_ER };
      }

      // 沒有說明得分的棒次，就預設下一棒時得分
      return { R: (order + 1) % 9, is_ER };
    };

    const getSB = (seq, order) => {
      const SB = parseInt(getCellValue(sheet[`T${seq}`]));
      const SBOrder = [
        parseInt(getCellValue(sheet[`U${seq}`])),
        parseInt(getCellValue(sheet[`V${seq}`])),
        parseInt(getCellValue(sheet[`W${seq}`])),
      ];

      const result = [];
      if (!(1 <= SB && SB <= 3)) {
        return result;
      }

      for (let i = 0; i < SB; i++) {
        if (1 <= SBOrder[i] && SBOrder[i] <= 9) {
          result.push(SBOrder[i]);
          continue;
        }
        // 沒有說明盜壘成功的棒次，就預設下一棒時盜壘成功
        result.push((order + 1) % 9);
      }

      return result;
    };

    const getCS = (seq, order) => {
      const CS = parseInt(getCellValue(sheet[`X${seq}`]));
      const CSOrder = parseInt(getCellValue(sheet[`Y${seq}`]));

      if (CS !== 1) {
        return 0;
      }

      if (1 <= CSOrder && CSOrder <= 9) {
        return CSOrder;
      }

      // 沒有說明盜壘失敗的棒次，就預設下一棒時盜壘失敗
      return (order + 1) % 9;
    };

    const getPO = (seq, order) => {
      const PO = parseInt(getCellValue(sheet[`Z${seq}`]));
      const POOrder = parseInt(getCellValue(sheet[`AA${seq}`]));

      if (PO !== 1) {
        return 0;
      }

      if (1 <= POOrder && POOrder <= 9) {
        return POOrder;
      }

      // 沒有說明牽制出局的棒次，就預設下一棒時牽制出局
      return (order + 1) % 9;
    };

    let seq = 2;
    let emptyLineTolerance = 3;
    while (emptyLineTolerance > 0) {
      seq++;
      if (getCellValue(sheet[`A${seq}`])) {
        emptyLineTolerance = 3;
      } else {
        emptyLineTolerance--;
        continue;
      }

      const batterNumber = getCellValue(sheet[`D${seq}`]);
      const order = parseInt(getCellValue(sheet[`E${seq}`]));
      const round = parseInt(getCellValue(sheet[`F${seq}`]));
      if (!batterNumber || Number.isNaN(order) || Number.isNaN(round)) {
        errorToast(`打席資訊 D${seq} E${seq} F${seq} 格式錯誤，打者背號、棒次與攻擊輪為必填`);
        return { error: true };
      }

      const batter = find((p) => p.number === batterNumber, targetPlayers);
      if (!batter) {
        console.log(targetPlayers);
        errorToast(`打席資訊 D${seq} 內容錯誤，打者背號需包含在球員名單內`);
        return { error: true };
      }

      const status = {
        inning: -1,
        inning_frame: inningFrame,
        pitcher: {},
        batter,
        PA_round: round,
        PA_order: order,
        pinch: '',
        pitcher_hand: ['L', 'R'].includes(getCellValue(sheet[`G${seq}`])) ? getCellValue(sheet[`G${seq}`]) : '',
        batter_hand: ['L', 'R'].includes(getCellValue(sheet[`H${seq}`])) ? getCellValue(sheet[`H${seq}`]) : '',
        away_score: -1,
        home_score: -1,
        start_outs: -1,
        outs: -1,
        bases: getBases(seq),
        ball_counts: [],
        drop_point: '',
        trajectory: '',
        RBI: 0,
        result: '',
        ...getR(seq, order), // R, is_ER
        R_count_on_pitcher: {}, // 失分算在非面對的投手身上時（換投後FC之類）
        SB: getSB(seq, order),
        CS: getCS(seq, order),
        PO: getPO(seq, order),
      };

      const pitcherNumber = getCellValue(sheet[`C${seq}`]);
      const pitcher = find((p) => p.number === pitcherNumber, opponentPlayers);
      if (!pitcher) {
        errorToast(`打席資訊 C${seq} 內容錯誤，投手背號需包含在球員名單內`);
        return { error: true };
      }
      status.pitcher = pitcher;

      const RCountOnPitcherNumber = getCellValue(sheet[`R${seq}`]);
      if (RCountOnPitcherNumber) {
        const RCountOnPitcher = find((p) => p.number === RCountOnPitcherNumber, opponentPlayers);
        if (!RCountOnPitcher) {
          errorToast(`打席資訊 R${seq} 內容錯誤，投手背號需包含在球員名單內`);
          return { error: true };
        }
        status.R_count_on_pitcher = RCountOnPitcher;
      }

      const mustNotEmptyColumn = { inning: 'B', away_score: 'I', home_score: 'J', start_outs: 'K', outs: 'L' };
      let emptyError = false;
      // eslint-disable-next-line no-loop-func
      Object.keys(mustNotEmptyColumn).forEach((key) => {
        const cellPosition = `${mustNotEmptyColumn[key]}${seq}`;
        const cell = sheet[cellPosition];
        if (!cell || !cell.v.toString()) {
          errorToast(`打席資訊 ${cellPosition} 格式錯誤，不應為空`);
          emptyError = true;
          return;
        }

        const value = parseInt(cell.v.toString());
        if (Number.isNaN(value) || value < 0) {
          errorToast(`打席資訊 ${cellPosition} 格式錯誤，應為零或正整數`);
          emptyError = true;
          return;
        }

        if (['start_outs', 'outs'].includes(key) && value > 2) {
          errorToast(`打席資訊 ${cellPosition} 格式錯誤，應為 0、1、2 其中之一`);
          emptyError = true;
          return;
        }

        status[key] = value;
      });
      if (emptyError) {
        return { error: true };
      }

      const originOrderBatter = find((PA) => PA.PA_order === order, pbpRows);
      if (originOrderBatter && originOrderBatter.batter.number !== batterNumber) {
        status.pinch = 'PH';
      }

      // 該局第一個要 0 out 0 bases
      if (!find((pbpRow) => pbpRow.inning === status.inning, pbpRows)) {
        if (status.outs !== 0 || status.bases !== 0) {
          errorToast(`打席資訊 I J K L${seq} 內容錯誤，該局首打者應該 0 出局壘上無人`);
          return { error: true };
        }
      }

      const PASeq = getCellValue(sheet[`A${seq}`]);
      if (PASeq === 'PR') {
        const originPAIndex = findLastIndex((PA) => PA.PA_order === order && PA.PA_round === round, pbpRows);
        if (originPAIndex < 0) {
          errorToast(`打席資訊 E${seq} F${seq} 格式錯誤，代跑找不到對應打者`);
          return { error: true };
        }

        if (originPAIndex === pbpRows.length - 1) {
          pbpRows.push({ ...status, pinch: 'PR' });
        } else {
          pbpRows.splice(originPAIndex + 1, 0, { ...status, pinch: 'PR' });
        }
        continue;
      }

      pbpRows.push(status);
    }
    return { pbp: pbpRows };
  };

  const parsePAResult = ({ sheet, pbp }) => {
    const pbpLength = pbp.length;
    let PRDelta = 0;
    for (let i = 0; i < pbpLength; i++) {
      const row = pbp[i];
      const seq = i + 3 - PRDelta;
      if (row.pinch === 'PR') {
        PRDelta++;
        continue;
      }

      // 未完成打席
      const PANO = getCellValue(sheet[`A${seq}`]);
      if (PANO === 'NONE') {
        continue;
      }

      const result = getCellValue(sheet[`E${seq}`]);
      if (!result) {
        errorToast(`打席結果 E${seq} 格式錯誤，不應為空`);
        return { error: true };
      }
      if (!ballInResult.includes(result) && !staticResult.includes(result)) {
        errorToast(`打席結果 E${seq} 格式錯誤，無相符事件`);
        return { error: true };
      }

      row.result = result;
      const RBI = parseInt(getCellValue(sheet[`D${seq}`]));
      row.RBI = Number.isNaN(RBI) ? 0 : RBI;

      if (staticResult.includes(result) && 'ID' !== result) {
        continue;
      }

      const dropPoint = getCellValue(sheet[`B${seq}`]);
      const trajectory = getCellValue(sheet[`C${seq}`]);
      // 妨礙守備可能有打球進場內，也可能沒有
      if ('ID' === result && !dropPoint && !trajectory) {
        continue;
      }

      if (!dropPoint) {
        errorToast(`打席結果 B${seq} 格式錯誤，不應為空`);
        return { error: true };
      }
      if (!dropPoints.includes(dropPoint)) {
        errorToast(`打席結果 B${seq} 格式錯誤，無相符落點`);
        return { error: true };
      }
      row.drop_point = dropPoint;

      if (!trajectory) {
        errorToast(`打席結果 C${seq} 格式錯誤，不應為空`);
        return { error: true };
      }
      if (!trajectoryMapping[trajectory]) {
        errorToast(`打席結果 C${seq} 格式錯誤，無相符軌跡`);
        return { error: true };
      }
      row.trajectory = trajectoryMapping[trajectory];
    }

    return { pbp };
  };

  const parsePABallCountsWithFielding = ({ sheet, pbp, opponentPlayers }) => {
    const ballEventMapping = {
      1: 'S', // Strike
      2: 'SW', // Swing
      3: 'B', // Ball
      4: 'F', // Foul
      5: 'H', // Hit
    };

    const fieldingPositions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'DH'];

    const isFieldingInfo = (cellValue) => {
      return cellValue.slice(0, 4) === 'POS:';
    };

    let totalBallCount = 0;
    const getBallCountsWithFielding = (seq) => {
      const ballCounts = [];
      const fielders = [];

      const possibleCounts = [];
      for (let j = 'B'.charCodeAt(0); j <= 'Z'.charCodeAt(0); j++) {
        possibleCounts.push(String.fromCharCode(j));
      }

      for (let j = 'A'.charCodeAt(0); j <= 'B'.charCodeAt(0); j++) {
        for (let k = 'A'.charCodeAt(0); k <= 'Z'.charCodeAt(0); k++) {
          possibleCounts.push(String.fromCharCode(j) + String.fromCharCode(k));
        }
      }

      const possibleCountLength = possibleCounts.length;
      for (let j = 0; j < possibleCountLength; j++) {
        const columnName = possibleCounts[j];
        const cellValue = getCellValue(sheet[`${columnName}${seq}`]);
        if (!cellValue) {
          return { ballCounts, fielders };
        }

        if (isFieldingInfo(cellValue)) {
          const fieldersInCell = cellValue.slice(4).trim().split(';');
          for (let fielderInCell of fieldersInCell) {
            const [fielderNumber, fielderPosition] = fielderInCell.split('>');
            let originFielder = null;
            let newFielderNumber = fielderNumber;
            // 有指定更換野手的原始打者
            if (fielderNumber.includes('|')) {
              const fielderNumbers = fielderNumber.split('|');
              const originFielderNumber = fielderNumbers[0];
              newFielderNumber = fielderNumbers[1];

              originFielder = find((p) => p.number === originFielderNumber, opponentPlayers);
              if (!originFielder) {
                errorToast(
                  `打席用球 ${columnName}${seq} 格式錯誤(${cellValue}) -> 無該選手(${fielderInCell})，請參考說明填寫`,
                );
                return { ballCounts: false, fielders: false };
              }
            }
            const player = find((p) => p.number === newFielderNumber, opponentPlayers);
            if (!player) {
              errorToast(
                `打席用球 ${columnName}${seq} 格式錯誤(${cellValue}) -> 無該選手(${fielderInCell})，請參考說明填寫`,
              );
              return { ballCounts: false, fielders: false };
            }
            if (!fieldingPositions.includes(fielderPosition)) {
              errorToast(
                `打席用球 ${columnName}${seq} 格式錯誤(${cellValue}) -> 無該守位(${fielderInCell})，請參考說明填寫`,
              );
              return { ballCounts: false, fielders: false };
            }

            fielders.push({
              player,
              position: fielderPosition === 'DH' ? 10 : parseInt(fielderPosition),
              in_play_ball_counts: totalBallCount,
              swapFrom: originFielder,
            });
          }
          continue;
        }

        if (!ballEventMapping[cellValue]) {
          errorToast(`打席用球 ${columnName}${seq} 格式錯誤(${cellValue})，請參考說明填寫`);
          return { ballCounts: false, fielders: false };
        }

        totalBallCount++;
        ballCounts.push(ballEventMapping[cellValue]);
      }

      return { ballCounts, fielders };
    };

    let opponentFielders = [];
    const pbpLength = pbp.length;
    let PRDelta = 0;
    for (let i = 0; i < pbpLength; i++) {
      const row = pbp[i];
      const seq = i + 3 - PRDelta;
      if (row.pinch === 'PR') {
        PRDelta++;
        continue;
      }

      const { ballCounts, fielders } = getBallCountsWithFielding(seq);
      if (ballCounts === false) {
        return { error: true };
      }
      row.ball_counts = ballCounts;
      opponentFielders = opponentFielders.concat(fielders);
    }

    return { pbp, opponentFielders };
  };

  // 雙面版
  const twoSideSheet = {
    away: {
      PAStatus: workbook.Sheets[`AWAY-${PAStatusSheetName}`],
      PAResult: workbook.Sheets[`AWAY-${PAResultSheetName}`],
      PABallCounts: workbook.Sheets[`AWAY-${PABallCountSheetName}`],
    },
    home: {
      PAStatus: workbook.Sheets[`HOME-${PAStatusSheetName}`],
      PAResult: workbook.Sheets[`HOME-${PAResultSheetName}`],
      PABallCounts: workbook.Sheets[`HOME-${PABallCountSheetName}`],
    },
  };

  if (
    !twoSideSheet.away.PAStatus ||
    !twoSideSheet.away.PAResult ||
    !twoSideSheet.away.PABallCounts ||
    !twoSideSheet.home.PAStatus ||
    !twoSideSheet.home.PAResult ||
    !twoSideSheet.home.PABallCounts
  ) {
    errorToast(
      `應包含 AWAY-${PAStatusSheetName} AWAY-${PAResultSheetName} AWAY-${PABallCountSheetName} 與 HOME- 共六張表單`,
    );
    return;
  }

  const parseOneSide = (inningFrame) => {
    const side = inningFrame === 'BOTTOM' ? 'home' : 'away';
    const opponentSide = inningFrame === 'BOTTOM' ? 'away' : 'home';

    const { pbp: pbpV1, error: errorStatus } = parsePAStatus({
      sheet: twoSideSheet[side].PAStatus,
      inningFrame,
      targetPlayers: players[side],
      opponentPlayers: players[opponentSide],
    });
    if (errorStatus) {
      return errorStatus;
    }

    const { pbp: pbpV2, error: errorResult } = parsePAResult({ sheet: twoSideSheet[side].PAResult, pbp: pbpV1 });
    if (errorResult) {
      return errorResult;
    }

    const { pbp: pbpV3, opponentFielders, error: errorBallCounts } = parsePABallCountsWithFielding({
      sheet: twoSideSheet[side].PABallCounts,
      pbp: pbpV2,
      opponentPlayers: players[opponentSide],
    });
    if (errorBallCounts) {
      return errorBallCounts;
    }

    PbPResult[side].pbp = pbpV3;
    PbPResult[opponentSide].fielders = opponentFielders;
    return false;
  };

  const gameInfo = allowPlayerCreate ? parseRoster(workbook.Sheets[RosterSheetName]) : null;
  if (allowPlayerCreate) {
    if (gameInfo.error) {
      return { error: true };
    }

    players.away = [...gameInfo.away.players];
    players.home = [...gameInfo.home.players];
  }

  const awayOnError = parseOneSide('TOP');
  if (awayOnError) {
    return { error: true };
  }

  const homeOnError = parseOneSide('BOTTOM');
  if (homeOnError) {
    return { error: true };
  }

  console.log(gameInfo);
  console.log(PbPResult.away.pbp);
  console.log(PbPResult.away.fielders);
  console.log(PbPResult.home.pbp);
  console.log(PbPResult.home.fielders);
  return {
    gameInfo,
    PbPResult,
  };
};

// 只處理投打
const pbpToBoxScore = ({
  awayPbP,
  homePbP,
  awayRuns,
  homeRuns,
  endOuts,
  lastPitcherSide,
  awayFielders,
  homeFielders,
}) => {
  const originPbP = {
    away: awayPbP,
    home: homePbP,
  };

  const originFielders = {
    away: awayFielders,
    home: homeFielders,
  };

  const BoxScore = {
    away: {
      batters: [],
      pitchers: [],
      pitchAmount: 0,
      scores: [],
    },
    home: {
      batters: [],
      pitchers: [],
      pitchAmount: 0,
      scores: [],
    },
  };

  const parseBoxScore = (side) => {
    const opponentSide = side === 'away' ? 'home' : 'away';

    const batterBoxScore = [];
    const opponentPitcherBoxScore = [];
    const accumulatedR = [];
    let prePAOut = 0;
    let prePAPitcherIndex = 0;
    let prePAInning = 1;

    let pitchAmount = 0;
    originPbP[side].forEach((PA) => {
      // 打席結果出來後的出局數，代跑有可能順序亂跑直接無視
      if (PA.start_outs !== prePAOut && PA.pinch !== 'PR') {
        if (PA.start_outs === 0) {
          opponentPitcherBoxScore[prePAPitcherIndex].IPOut += 3 - prePAOut;
        } else {
          opponentPitcherBoxScore[prePAPitcherIndex].IPOut += PA.start_outs - prePAOut;
        }
      }

      // 更換局數後，第一個打席的分數就是前一局的最後分數
      if (PA.inning !== prePAInning) {
        BoxScore[side].scores.push(PA[`${side}_score`] - sum(BoxScore[side].scores));
      }

      let batterIndex = findIndex((b) => b.player.uniqid === PA.batter.uniqid, batterBoxScore);
      let pitcherIndex = findIndex((p) => p.player.uniqid === PA.pitcher.uniqid, opponentPitcherBoxScore);

      if (batterIndex < 0) {
        batterBoxScore.push({
          player: { name: PA.batter.name, number: PA.batter.number, uniqid: PA.batter.uniqid },
          order: PA.PA_order,
          is_PH: !!PA.pinch,
          PA: 0,
          AB: 0,
          H: 0,
          TWOB: 0,
          THREEB: 0,
          HR: 0,
          SO: 0,
          BB: 0,
          IBB: 0,
          HBP: 0,
          SF: 0,
          SH: 0,
          GIDP: 0,
          FC: 0,
          E: 0,
          FO: 0,
          GO: 0,
          R: 0,
          RBI: 0,
          SB: 0,
          CS: 0,
        });
        batterIndex = batterBoxScore.length - 1;
      }

      if (pitcherIndex < 0) {
        let R = 0;
        let ER = 0;
        filter(
          (r) =>
            r.pitcherUniqid === PA.pitcher.uniqid &&
            r.pitcherNumber === PA.pitcher.number &&
            r.pitcherName === PA.pitcher.name,
          accumulatedR,
        ).forEach((r) => {
          R++;
          if (r.isER) {
            ER++;
          }
        });

        opponentPitcherBoxScore.push({
          player: { name: PA.pitcher.name, number: PA.pitcher.number, uniqid: PA.pitcher.uniqid },
          order: opponentPitcherBoxScore.length + 1,
          IPOut: 0,
          NP: 0,
          BF: 0,
          H: 0,
          HR: 0,
          BB: 0,
          IBB: 0,
          HB: 0,
          SO: 0,
          WP: 0,
          BK: 0,
          R: R,
          ER: ER,
        });
        pitcherIndex = opponentPitcherBoxScore.length - 1;
      }

      const batter = batterBoxScore[batterIndex];
      const pitcher = opponentPitcherBoxScore[pitcherIndex];
      // 打席中間的出局數
      pitcher.IPOut += PA.outs - PA.start_outs;
      pitcher.NP += PA.ball_counts.length;
      pitchAmount += PA.ball_counts.length;

      batter.SB += PA.SB.length;
      batter.CS += PA.CS > 0 ? 1 : 0;
      if (PA.R > 0) {
        batter.R++;

        const addPitcherR = (targetPitcher) => {
          targetPitcher.R++;
          if (PA.is_ER) {
            targetPitcher.ER++;
          }
        };

        // 非面對投手的鍋
        if (PA.R_count_on_pitcher.uniqid) {
          const RCountOnPitcherIndex = findIndex(
            (p) => p.player.uniqid === PA.R_count_on_pitcher.uniqid,
            opponentPitcherBoxScore,
          );
          if (RCountOnPitcherIndex >= 0) {
            addPitcherR(opponentPitcherBoxScore[RCountOnPitcherIndex]);
          } else {
            accumulatedR.push({
              isER: PA.is_ER,
              pitcherUniqid: PA.R_count_on_pitcher.uniqid,
              pitcherNumber: PA.R_count_on_pitcher.number,
              pitcherName: PA.R_count_on_pitcher.name,
            });
          }
        } else {
          addPitcherR(pitcher);
        }
      }

      if (PA.result !== '') {
        batter.PA++;
        batter.RBI += PA.RBI;
        pitcher.BF++;

        switch (PA.result) {
          case '1B':
            batter.H++;
            batter.AB++;
            pitcher.H++;
            break;
          case '2B':
            batter.H++;
            batter.TWOB++;
            batter.AB++;
            pitcher.H++;
            break;
          case '3B':
            batter.H++;
            batter.THREEB++;
            batter.AB++;
            pitcher.H++;
            break;
          case 'HR':
            batter.H++;
            batter.HR++;
            batter.AB++;
            pitcher.H++;
            pitcher.HR++;
            break;
          case 'SH':
          case 'SF':
            batter[PA.result]++;
            break;
          case 'HBP':
            batter[PA.result]++;
            pitcher.HB++;
            break;
          case 'IBB':
            batter.IBB++;
            pitcher.IBB++;
          // fallthrough
          case 'uBB':
            batter.BB++;
            pitcher.BB++;
            break;
          case 'SO':
            batter.SO++;
            batter.AB++;
            pitcher.SO++;
            break;
          default:
            // 不記錄且無打數
            if (['IR', 'IH'].includes(PA.result)) {
              break;
            }

            // 不記錄但有打數
            if (['ID'].includes(PA.result)) {
              batter.AB++;
              break;
            }

            // 正常加結果加打數
            batter[PA.result]++;
            batter.AB++;
            break;
        }
      }

      if (PA.pinch !== 'PR') {
        prePAOut = PA.outs;
        prePAPitcherIndex = pitcherIndex;
        prePAInning = PA.inning;
      }
    });
    // 先假設最後都是 3 出局，晚點再依照 endOuts 刪減
    opponentPitcherBoxScore[opponentPitcherBoxScore.length - 1].IPOut += 3 - prePAOut;

    // 沒有上場打的野手如果有上場紀錄也有更換對象就幫忙補上去
    originFielders[side].forEach((fielder) => {
      if (!fielder.swapFrom) {
        return;
      }
      const originFielder = fielder.swapFrom;
      const originFielderInBoxIndex = findIndex(
        (batterInBox) =>
          batterInBox.player.name === originFielder.name &&
          batterInBox.player.number === originFielder.number &&
          batterInBox.player.uniqid === originFielder.uniqid,
        batterBoxScore,
      );
      if (originFielderInBoxIndex < 0) {
        // 可能是不在打線的投手或是誤植
        return;
      }

      if (
        findIndex(
          (batterInBox) =>
            batterInBox.player.name === fielder.player.name &&
            batterInBox.player.number === fielder.player.number &&
            batterInBox.player.uniqid === fielder.player.uniqid,
          batterBoxScore,
        ) >= 0
      ) {
        // 有在 BoxScore 裡面，不重複新增
        return;
      }

      // 這野手有上來守備，沒上去打擊，幫他塞一個全 0 的紀錄
      const emptyBox = {
        player: { name: fielder.player.name, number: fielder.player.number, uniqid: fielder.player.uniqid },
        order: batterBoxScore[originFielderInBoxIndex].order,
        is_PH: true,
        PA: 0,
        AB: 0,
        H: 0,
        TWOB: 0,
        THREEB: 0,
        HR: 0,
        SO: 0,
        BB: 0,
        IBB: 0,
        HBP: 0,
        SF: 0,
        SH: 0,
        GIDP: 0,
        FC: 0,
        E: 0,
        FO: 0,
        GO: 0,
        R: 0,
        RBI: 0,
        SB: 0,
        CS: 0,
      };
      if (!batterBoxScore[originFielderInBoxIndex + 1]) {
        batterBoxScore.push(emptyBox);
      } else {
        batterBoxScore.splice(originFielderInBoxIndex + 1, 0, emptyBox);
      }
    });

    // 對手投手如果有上場紀錄但沒有完成任何 PA，幫忙補上去
    originFielders[opponentSide].forEach((fielder) => {
      if (!fielder.swapFrom || fielder.position !== 1) {
        return;
      }

      const originPitcher = fielder.swapFrom;
      const originPitcherInBoxIndex = findIndex(
        (pitcherInBox) =>
          pitcherInBox.player.name === originPitcher.name &&
          pitcherInBox.player.number === originPitcher.number &&
          pitcherInBox.player.uniqid === originPitcher.uniqid,
        opponentPitcherBoxScore,
      );
      if (originPitcherInBoxIndex < 0) {
        // 可能誤植
        return;
      }

      if (
        findIndex(
          (pitcherInBox) =>
            pitcherInBox.player.name === fielder.player.name &&
            pitcherInBox.player.number === fielder.player.number &&
            pitcherInBox.player.uniqid === fielder.player.uniqid,
          opponentPitcherBoxScore,
        ) >= 0
      ) {
        // 有在 BoxScore 裡面，不重複新增
        return;
      }

      let R = 0;
      let ER = 0;
      filter(
        (r) =>
          r.pitcherUniqid === fielder.player.uniqid &&
          r.pitcherNumber === fielder.player.number &&
          r.pitcherName === fielder.player.name,
        accumulatedR,
      ).forEach((r) => {
        R++;
        if (r.isER) {
          ER++;
        }
      });

      const emptyBox = {
        player: { name: fielder.player.name, number: fielder.player.number, uniqid: fielder.player.uniqid },
        order: opponentPitcherBoxScore[originPitcherInBoxIndex].order + 1,
        IPOut: 0,
        NP: 0,
        BF: 0,
        H: 0,
        HR: 0,
        BB: 0,
        IBB: 0,
        HB: 0,
        SO: 0,
        WP: 0,
        BK: 0,
        R: R,
        ER: ER,
      };

      if (!opponentPitcherBoxScore[originPitcherInBoxIndex + 1]) {
        opponentPitcherBoxScore.push(emptyBox);
      } else {
        opponentPitcherBoxScore.splice(originPitcherInBoxIndex + 1, 0, emptyBox);
      }

      // 重新配一次排序
      opponentPitcherBoxScore.forEach((pitcherBox, index) => {
        pitcherBox.order = index + 1;
      });
    });

    BoxScore[side].batters = batterBoxScore;
    BoxScore[opponentSide].pitchers = opponentPitcherBoxScore;
    BoxScore[opponentSide].pitchAmount = pitchAmount;
  };

  parseBoxScore('away');
  parseBoxScore('home');

  BoxScore.away.scores.push(awayRuns - sum(BoxScore.away.scores));
  BoxScore.home.scores.push(homeRuns - sum(BoxScore.home.scores));

  // 結束時不是 3 出局的要額外處理，原本都給 3 了要扣回來
  if (endOuts !== 3) {
    const lastPitcher = BoxScore[lastPitcherSide].pitchers[BoxScore[lastPitcherSide].pitchers.length - 1];
    console.log(lastPitcher);
    console.log(lastPitcher.IPOut);
    console.log(endOuts);
    lastPitcher.IPOut -= 3 - endOuts;
  }

  return BoxScore;
};

export const getBoxScore = ({ gameInfo, parsedData, endOuts, errorToast }) => {
  // -1 -1 => away
  // -1 8 => home
  // 7 6 => home
  const awayLastInning = findIndex((s) => s === '-', gameInfo.away.scores);
  const homeLastInning = findIndex((s) => s === '-', gameInfo.home.scores);
  const lastPitcherSide = awayLastInning === homeLastInning ? 'away' : 'home';

  const BPBoxScore = pbpToBoxScore({
    awayPbP: parsedData.away.pbp,
    homePbP: parsedData.home.pbp,
    awayRuns: gameInfo.away.runs,
    homeRuns: gameInfo.home.runs,
    endOuts,
    lastPitcherSide,
    awayFielders: parsedData.away.fielders ?? [],
    homeFielders: parsedData.home.fielders ?? [],
  });

  const BoxScore = {
    away: {
      ...BPBoxScore.away,
      pbp: parsedData.away.pbp,
      players: [],
    },
    home: {
      ...BPBoxScore.home,
      pbp: parsedData.home.pbp,
      players: [],
    },
    end_outs: endOuts,
  };

  const parseFielding = (side) => {
    const pitcherBoxScore = BoxScore[side].pitchers;
    const pitchAmount = BoxScore[side].pitchAmount;
    const playerBoxScore = [];

    let currentPitcherIndex = -1;
    parsedData[side].fielders.forEach((fielding) => {
      const boxPosition = pbpPositionToBox[fielding.position];

      let playerIndex = findIndex((p) => p.info.uniqid === fielding.player.uniqid, playerBoxScore);
      if (playerIndex < 0) {
        playerBoxScore.push({
          info: { name: fielding.player.name, number: fielding.player.number, uniqid: fielding.player.uniqid },
          fieldings: [],
        });
        playerIndex = playerBoxScore.length - 1;
      }

      const player = playerBoxScore[playerIndex];
      player.fieldings.push({
        position: boxPosition,
        order: player.fieldings.length + 1,
        PO: 0,
        A: 0,
        E: 0,
        DP: 0,
        PB: 0,
        SB: 0,
        CS: 0,
        in_play_ball_counts: fielding.in_play_ball_counts,
      });

      // 換投時，塞入前一任投手的球數
      if (boxPosition !== 'P') {
        return;
      }

      if (currentPitcherIndex < 0) {
        currentPitcherIndex = playerIndex;
        return;
      }

      const currentPicherBoxScoreIndex = findIndex(
        (p) => p.player.uniqid === playerBoxScore[currentPitcherIndex].info.uniqid,
        pitcherBoxScore,
      );
      const currentPitcherFieldings = playerBoxScore[currentPitcherIndex].fieldings;
      const currentPicherFieldingIndex = currentPitcherFieldings.length - 1;

      currentPitcherIndex = playerIndex;
      if (currentPicherBoxScoreIndex < 0) {
        // 可能投不到一個人次又下去了，所以投球內容完全沒有這人
        return;
      }

      const currentPicherBoxScore = pitcherBoxScore[currentPicherBoxScoreIndex];
      // 本場比賽第一次擔任投手，要把原本用打者算的球數清除
      if (filter((f) => f.position === 'P', currentPitcherFieldings).length === 1) {
        currentPicherBoxScore.NP = 0;
      }
      currentPicherBoxScore.NP +=
        fielding.in_play_ball_counts - currentPitcherFieldings[currentPicherFieldingIndex].in_play_ball_counts;
    });
    // 最後一任投手要把剩下的球都送給他
    if (currentPitcherIndex >= 0) {
      const lastPicherBoxScoreIndex = find(
        (p) => p.player.uniqid === playerBoxScore[currentPitcherIndex].info.uniqid,
        pitcherBoxScore,
      );
      if (lastPicherBoxScoreIndex >= 0) {
        const lastPitcherFieldings = playerBoxScore[currentPitcherIndex].fieldings;
        const lastPicherFieldingIndex = lastPitcherFieldings.length - 1;
        const lastPicherBoxScore = pitcherBoxScore[lastPicherBoxScoreIndex];
        if (filter((f) => f.position === 'P', lastPitcherFieldings).length === 1) {
          lastPicherBoxScore.NP = 0;
        }
        lastPicherBoxScore.NP += pitchAmount - lastPitcherFieldings[lastPicherFieldingIndex].in_play_ball_counts;
      }
    }

    BoxScore[side].players = playerBoxScore;
  };

  parseFielding('away');
  parseFielding('home');

  // 驗證每局得分跟記分板填的是不是一樣
  for (let i = 0; i < BoxScore.away.scores.length; i++) {
    const acceptScoreWordings = [BoxScore.away.scores[i].toString(), `${BoxScore.away.scores[i].toString()}x`];
    if (acceptScoreWordings.includes(gameInfo.away.scores[i])) {
      continue;
    }

    errorToast(`客場第${i + 1}局得分跟計分板有出入，請確認資料`);
    return false;
  }
  for (let i = 0; i < BoxScore.home.scores.length; i++) {
    const acceptScoreWordings = [BoxScore.home.scores[i].toString(), `${BoxScore.home.scores[i].toString()}x`];
    if (acceptScoreWordings.includes(gameInfo.home.scores[i])) {
      continue;
    }

    errorToast(
      `主場第${i + 1}局得分(${BoxScore.home.scores[i].toString()})` +
        `跟計分板(${gameInfo.home.scores[i]})有出入，請確認資料`,
    );
    return false;
  }

  return BoxScore;
};
